import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { FlexColumn } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { SimpleModal } from '../../simple-modal/simple-modal';
import { messages } from './messages';
import styles from './source-id-search.module.less';

import type { SelectedOrder, SalesOrder, FulfillmentPlan } from './types';
import { getActivePlans } from './types';

const MODAL_SIZE = 'medium';

type OrderSelectionModalProps = {
  salesOrders: SalesOrder[];
  searchText: string;
  onSelect: (order: SelectedOrder) => void;
  onClose: () => void;
};

const buildSelectedOrder = (order: SalesOrder, plan: FulfillmentPlan): SelectedOrder => ({
  sourceIds: [Number(plan.fulfillmentPlanId)],
  orderNumber: order.orderNumber,
  recipientName: order.recipientName ?? '',
});

export const OrderSelectionModal = ({ salesOrders, searchText, onSelect, onClose }: OrderSelectionModalProps) => {
  const intl = useIntl();

  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrder | null>(salesOrders.length === 1 ? salesOrders[0] : null);
  const [selectedPlan, setSelectedPlan] = useState<FulfillmentPlan | null>(null);

  const activePlans = selectedSalesOrder ? getActivePlans(selectedSalesOrder) : [];

  const isShowingShipments = selectedSalesOrder !== null && salesOrders.length === 1 && activePlans.length > 1;
  const isShowingOrders = !isShowingShipments;

  const handleAction = useCallback(() => {
    if (isShowingOrders && selectedSalesOrder) {
      const plans = getActivePlans(selectedSalesOrder);
      if (plans.length === 1) {
        onSelect(buildSelectedOrder(selectedSalesOrder, plans[0]));
        return;
      }
      setSelectedPlan(plans[0]);
      return;
    }

    if (!isShowingOrders && selectedPlan && selectedSalesOrder) {
      onSelect(buildSelectedOrder(selectedSalesOrder, selectedPlan));
    }
  }, [isShowingOrders, selectedSalesOrder, selectedPlan, onSelect]);

  const bodyMessage = isShowingShipments
    ? intl.formatMessage(messages.selectShipmentBody)
    : intl.formatMessage(messages.selectOrderBody, { orderNumber: searchText });

  const isActionDisabled = isShowingOrders ? !selectedSalesOrder : !selectedPlan;

  return (
    <SimpleModal
      actionButtonDataPendoId='order-selection-modal-continue'
      actionMessage={messages.continueButton}
      cancelMessage={messages.cancelButton}
      headerMessage={messages.selectOrderTitle}
      isActionDisabled={isActionDisabled}
      onAction={handleAction}
      onCancel={onClose}
      onClose={onClose}
      size={MODAL_SIZE}
    >
      <FlexColumn gap={Gap.Space12}>
        <Text textType={TextType.Description} value={bodyMessage} />

        {isShowingOrders
          ? salesOrders.map((order) => {
              const uniqueItemsCount = getActivePlans(order).reduce((sum, p) => sum + (p.items?.length || 0), 0);

              return (
                <SelectableRow
                  key={order.salesOrderId}
                  isSelected={selectedSalesOrder?.salesOrderId === order.salesOrderId}
                  label={`#${order.orderNumber} — ${order.recipientName || 'N/A'}`}
                  onClick={() => setSelectedSalesOrder(order)}
                  sublabel={intl.formatMessage(messages.itemCount, { count: uniqueItemsCount })}
                />
              );
            })
          : activePlans.map((plan) => {
              const itemsCount = plan.items?.length || 0;
              return (
                <SelectableRow
                  key={plan.fulfillmentPlanId}
                  isSelected={selectedPlan?.fulfillmentPlanId === plan.fulfillmentPlanId}
                  label={`Shipment #${plan.shipmentNumber}`}
                  onClick={() => setSelectedPlan(plan)}
                  sublabel={intl.formatMessage(messages.itemCountWithStatus, { count: itemsCount, status: plan.orderGridStatus })}
                />
              );
            })}
      </FlexColumn>
    </SimpleModal>
  );
};

type SelectableRowProps = {
  isSelected: boolean;
  label: string;
  sublabel: string;
  onClick: () => void;
};

const SelectableRow = ({ isSelected, label, sublabel, onClick }: SelectableRowProps) => (
  <FlexRow className={classNames(styles['selectable-row'], { [styles.selected]: isSelected })} gap={Gap.Space8} onClick={onClick}>
    <FlexColumn>
      <Text textType={TextType.Caption} value={label} />
      <Text textType={TextType.Caption} value={sublabel} />
    </FlexColumn>
  </FlexRow>
);
